import React, { createContext, useContext, useState, useMemo, useEffect } from "react";
import { useGetLensesContext } from "./hooks/useGetLensesContext";

/**
 * LensesV2 dashboard context.
 *
 * Holds the shared filtration state for the whole Lenses 2 tab — the period
 * options and the currently selected period — and exposes the `findQuery` that
 * EVERY widget passes to its data hook. The filter bar writes the period;
 * widgets only read `findQuery`. The findQuery itself is baked into each period
 * option by the context endpoint, so there's no query construction here.
 */

const LensesV2Context = createContext(null);

export const useLensesV2Context = () => {
  const ctx = useContext(LensesV2Context);
  if (!ctx) throw new Error("useLensesV2Context must be used within <LensesV2ContextProvider>");
  return ctx;
};

export const LensesV2ContextProvider = ({ children }) => {
  const { data: context } = useGetLensesContext();

  // "all_time" is excluded here on purpose: every LensesV2 widget charts a
  // trend against the previous equal-length window, and all-time has no
  // previous window (the BE ships it with prevFindQuery: null). It exists for
  // consumers that only narrow (the Topics tab).
  const periodOptions = useMemo(
    () => (context?.date?.periods || []).filter((p) => p.value !== "all_time"),
    [context]
  );

  const [period, setPeriod] = useState(null);

  // Default the period to the context's defaultPeriod once it arrives.
  useEffect(() => {
    if (period || !context?.date) return;
    const def =
      periodOptions.find((p) => p.value === context.date.defaultPeriod) ||
      periodOptions[0] ||
      null;
    if (def) setPeriod(def);
  }, [context, periodOptions, period]);

  const findQuery = period?.findQuery;
  const prevFindQuery = period?.prevFindQuery; // previous same-length window, for trend
  const signals = context?.signals || []; // [{ _id, display, category }] — full signal set
  const topics = context?.topics || []; // [{ _id, display }] — the full topic set
  const features = context?.features || []; // [{ _id, display }] — the full feature set
  const emotionAffect = context?.emotionAffect || []; // [{ _id, display, tagId }] — emotion & affect tags
  const demographics = context?.demographics || null; // { linkedDataset, dimensions, _debug } — segment-by dropdown
  const dateFieldMap = context?.date?.dateFieldMap || {}; // per-CT { valuePath, dateAsString }

  const value = useMemo(
    () => ({ periodOptions, period, setPeriod, findQuery, prevFindQuery, signals, topics, features, emotionAffect, demographics, dateFieldMap }),
    [periodOptions, period, findQuery, prevFindQuery, signals, topics, features, emotionAffect, demographics, dateFieldMap]
  );

  return <LensesV2Context.Provider value={value}>{children}</LensesV2Context.Provider>;
};
